/**
 * 把后端拒绝的**英文整段**换成界面语言的文案。
 *
 * 为什么需要这一层：拒绝文案是 `@mengyyy369/medusa-paypal` 里的模板字符串
 * （`engine.js:263` 之类），不经过我们任何 i18n 资源；而 `refusalMessage()`
 * 又会把它原样透传到 toast。结果是中文界面弹一整段英文（P0-3，2026-10-25
 * 实测复现）。这里只做「翻译」，不改任何请求/响应语义。
 *
 * 匹配优先级：
 *   1. **错误码** —— 引擎抛错时带 `code`（例如 `SUBSCRIPTION_ALREADY_ACTIVE`），
 *      它比英文措辞稳定，改文案不会失配。
 *   2. **措辞** —— 没有 `code` 的那几条（MedusaError 没传第三参）只能按句式匹配。
 *   3. 都不中 → 返回 `undefined`，调用方回落到后端原文。宁可显示英文，
 *      也不要把「钱没付成功」的原因吞成一句泛泛的「出错了」。
 */

export const REFUSAL_KEYS = [
  "activeSubscription",
  "paypalNotActive",
  "subscriptionQuantity",
  "subscriptionMixedItems",
  "subscriptionMultipleItems",
  "subscriptionPlanSwitch",
  "trialAlreadyClaimed",
  "trialNeedsDiscord",
  "unbindLastMethod",
] as const

export type RefusalKey = (typeof REFUSAL_KEYS)[number]

/** 引擎的错误码 → 文案 key。 */
const CODE_KEYS: Record<string, RefusalKey> = {
  SUBSCRIPTION_ALREADY_ACTIVE: "activeSubscription",
  SUBSCRIPTION_NOT_ACTIVE: "paypalNotActive",
}

type Rule = {
  pattern: RegExp
  key: RefusalKey
  /** 从匹配里把商品名捞出来，供文案里的 `{product}` 用。 */
  values?: (match: RegExpMatchArray) => Record<string, string>
}

/**
 * 从「已订阅」那两句英文里摘出商品名。
 *
 * **为什么需要两个句式**（A2）：引擎有两条不同的话，商品名位置不一样——
 *
 *   1. 支付引擎（`engine.js:263`）：
 *      `You already have an active subscription for DayZ PBO Protector. Cancel it first…`
 *   2. reorder 的结算闸门（`checkout-gate.js:18-22`）：
 *      `You already have an active subscription for 'Pro' on this account
 *       (subscription sub_x). A product can be covered by only one active…`
 *
 * 旧写法用非锚定的 `(.+?)\.`，在句式 2 上会截到**第一个句号**为止，把商品名捕成
 * `'Pro' on this account (subscription sub_x)`，再把一整串带内部 id 的垃圾
 * 填进 `{product}`。现在两条句式各用一条规则，且都必须匹配到句子尾部
 * （`Cancel it first` / `on this account (subscription …)`）才算命中。
 */
const productFromActiveSubscription = (message?: string):
  | Record<string, string>
  | undefined => {
  if (!message) return undefined
  const quoted = message.match(
    /You already have an active subscription for '(.+?)' on this account \(subscription \S+\)/i
  )
  if (quoted) return { product: quoted[1] }
  const plain = message.match(
    /You already have an active subscription for (.+?)\. Cancel it first/i
  )
  if (plain) return { product: plain[1] }
  return undefined
}

/** 没有错误码的那几条，只能按句式认。 */
const MESSAGE_RULES: Rule[] = [
  {
    pattern: /You already have an active subscription for (.+?)\. Cancel it first/i,
    key: "activeSubscription",
    values: (m) => ({ product: m[1] }),
  },
  {
    pattern:
      /You already have an active subscription for '(.+?)' on this account \(subscription \S+\)/i,
    key: "activeSubscription",
    values: (m) => ({ product: m[1] }),
  },
  {
    pattern: /A subscription can only be purchased with a quantity of 1/i,
    key: "subscriptionQuantity",
  },
  {
    pattern: /Subscription items cannot be combined with regular items/i,
    key: "subscriptionMixedItems",
  },
  {
    pattern: /Multiple different subscription items are not supported/i,
    key: "subscriptionMultipleItems",
  },
  {
    pattern:
      /A subscription can only switch to another plan of the same product/i,
    key: "subscriptionPlanSwitch",
  },
  // 试用领取的两条（A3）。原句都由 `@mengyyy369/reorder` 的
  // `TRIAL_CLAIM_CUSTOMER_REFUSALS` 声明为可原文外泄的拒绝：
  //   - `modules/trial-claim/service.js:18` 的重复领取；
  //   - `workflows/utils/discord-gate.js` 的入服门槛。
  // 两句里只有一个内部 id（cus_/prod_），**不要**把它塞进文案。
  {
    pattern:
      /^Trial has already been claimed for customer \S+ and product \S+$/i,
    key: "trialAlreadyClaimed",
  },
  {
    pattern: /^This trial requires joining the Discord server first\.$/i,
    key: "trialNeedsDiscord",
  },
  // 解绑的锁死守卫（A5）。`@mengyyy369/medusa-better-auth` 的
  // `lib/ba-admin.js:379` 定义 `UNBIND_LAST_LOGIN_METHOD_MESSAGE`，由 store 路由
  // 以 422 原样回给店面；不翻译就会在中文界面露这一整句英文。
  {
    pattern:
      /^unbinding would leave this user with no available way to sign in\.?$/i,
    key: "unbindLastMethod",
  },
]

export type RefusalCopy = {
  key: RefusalKey
  values?: Record<string, string>
}

/**
 * 判定一条后端拒绝该怎么翻译。命不中返回 `null`。
 *
 * `message` 只在拿不到 `code` 时才参与匹配 —— 有码就别看措辞。
 */
export const resolveRefusalCopy = (input: {
  code?: string
  message?: string
}): RefusalCopy | null => {
  if (input.code) {
    const key = CODE_KEYS[input.code]
    // 有码但没登记：交给措辞匹配去试（码可能来自引擎的其它版本）。
    if (key) {
      return {
        key,
        values: productFromActiveSubscription(input.message),
      }
    }
  }

  if (!input.message) return null

  for (const rule of MESSAGE_RULES) {
    const match = input.message.match(rule.pattern)
    if (match) return { key: rule.key, values: rule.values?.(match) }
  }

  return null
}
